import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Sheet,
  Table,
  Typography,
} from '@mui/joy';
import type { DependentCount } from '../types';
import { formatDependents } from '../data/repository';
import { ConfirmDialog, Toolbar } from './FormControls';

export interface NamedRow {
  id: string;
  name: string;
}

export function NamedEntitySection({
  title,
  nameLabel,
  rows,
  loading,
  error,
  onReload,
  onCreate,
  onUpdate,
  onDelete,
  dependentsFor,
}: {
  title: string;
  nameLabel: string;
  rows: NamedRow[];
  loading: boolean;
  error: string | null;
  onReload: () => void;
  onCreate: (input: { name: string }) => Promise<unknown>;
  onUpdate: (id: string, input: { name: string }) => Promise<unknown>;
  onDelete: (id: string) => Promise<void>;
  dependentsFor: (id: string) => Promise<DependentCount[]>;
}) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<NamedRow | null>(null);
  const [name, setName] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteMessage, setDeleteMessage] = useState('');

  const reset = () => {
    setEditing(null);
    setName('');
    setFormError(null);
  };

  const openCreate = () => {
    reset();
    setOpen(true);
  };

  const openEdit = (row: NamedRow) => {
    setEditing(row);
    setName(row.name);
    setFormError(null);
    setOpen(true);
  };

  const save = async () => {
    if (!name.trim()) {
      setFormError(`${nameLabel} is required.`);
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const payload = { name: name.trim() };
      if (editing) await onUpdate(editing.id, payload);
      else await onCreate(payload);
      setOpen(false);
      reset();
      onReload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const requestDelete = async (row: NamedRow) => {
    const dependents = await dependentsFor(row.id);
    if (dependents.length) {
      setFormError(`Cannot delete “${row.name}” while it is used by ${formatDependents(dependents)}.`);
      return;
    }
    setDeleteId(row.id);
    setDeleteMessage(`Delete “${row.name}”? This cannot be undone.`);
  };

  return (
    <Box>
      {error ? <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert> : null}
      {formError && !open ? <Alert color="warning" sx={{ mb: 2 }}>{formError}</Alert> : null}
      <Toolbar>
        <Button onClick={openCreate}>Add {title.replace(/s$/, '')}</Button>
      </Toolbar>
      <Sheet variant="outlined" sx={{ overflow: 'auto' }}>
        <Table stickyHeader>
          <thead>
            <tr>
              <th>{nameLabel}</th>
              <th style={{ width: 180 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={2}>Loading…</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={2}>None yet.</td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.name}</td>
                  <td>
                    <Button size="sm" variant="plain" onClick={() => openEdit(row)}>
                      Edit
                    </Button>
                    <Button size="sm" variant="plain" color="danger" onClick={() => requestDelete(row)}>
                      Delete
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Sheet>

      <Modal open={open} onClose={() => setOpen(false)}>
        <ModalDialog sx={{ minWidth: 420 }}>
          <ModalClose />
          <Typography level="title-lg">{editing ? `Edit ${title.replace(/s$/, '')}` : `New ${title.replace(/s$/, '')}`}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            {formError ? <Alert color="danger">{formError}</Alert> : null}
            <Input placeholder={nameLabel} value={name} onChange={(event) => setName(event.target.value)} />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button variant="outlined" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button loading={saving} onClick={save}>
                Save
              </Button>
            </Box>
          </Box>
        </ModalDialog>
      </Modal>

      <ConfirmDialog
        open={Boolean(deleteId)}
        title={`Delete ${title.replace(/s$/, '')}?`}
        message={deleteMessage}
        onClose={() => setDeleteId(null)}
        onConfirm={async () => {
          if (!deleteId) return;
          try {
            await onDelete(deleteId);
            setDeleteId(null);
            onReload();
          } catch (err) {
            setDeleteId(null);
            setFormError(err instanceof Error ? err.message : 'Delete failed');
          }
        }}
      />
    </Box>
  );
}

export function useAsyncList<T>(loader: () => Promise<T[]>) {
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = () => {
    setLoading(true);
    loader()
      .then((data) => {
        setRows(data);
        setError(null);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Load failed'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { rows, loading, error, reload, setRows };
}
